<template>
  <div
    ref="route"
    class="route"
    :class="[mode, { details }]"
    :style="{ left: timelineStart }"
    @click="openRoute"
  >
    <slot v-if="route.current">
      <div ref="truck" class="truck-svg" :style="truckStyle">
        <img
          v-if="route.truck"
          :src="$getSvgUrl(route.truck)"
          :class="{ 'truck-move': truckMoving }"
        />
      </div>
    </slot>
    <div class="actions">
      <slot v-for="(operation, index) in route.operations">
        <div class="action" :key="operation.id">
          <slot v-if="operation.company">
            <div
              v-if="index > 0"
              class="progress"
              :style="{ width: timelineRange(index) }"
            >
              <slot v-if="isPending">
                <div class="progress-bar w-full" />
              </slot>
              <slot v-else>
                <div
                  class="progress-bar"
                  :style="{ width: durationRatio(index) + '%' }"
                  :class="{ departed: route.operations[index - 1].departed }"
                />
                <div
                  class="progress-bar road"
                  :style="{ width: hoursRatio(index) + '%' }"
                  :class="{
                    finished: operation.arrived,
                    ticked: route.operations[index - 1].departed,
                  }"
                />
              </slot>
            </div>
            <div
              class="stop"
              :class="{
                even: operation.stop % 2 == 0,
                ticked: operation.arrived || index == 0,
              }"
              :data-id="operation.company?.id"
            >
              <el-tooltip
                placement="top"
                :content="operation.company?.name"
                :open-delay="500"
              >
                <div
                  class="icon"
                  v-html="rawSvg(operation).path"
                  :style="{ color: rawSvgColor(operation) }"
                  @mouseenter="enterCompany(operation.company)"
                  @mouseleave="leaveCompany()"
                />
              </el-tooltip>
              <div class="company">
                {{
                  operation.company?.type == "Warehouse"
                    ? operation.company?.city_state
                    : operation.company?.name
                }}
              </div>
            </div>
          </slot>
        </div>
      </slot>
    </div>
    <slot v-if="!isPending">
      <div class="abbrev-type" v-if="route.container?.abbrev_type">
        {{ route.container.abbrev_type }}
      </div>
    </slot>
  </div>
</template>
<script>
export default {
  name: "XmapRoute",
  props: {
    route: {
      type: Object,
      default: () => ({}),
    },
    details: {
      type: Boolean,
      default: false,
    },
    timelineWidth: {
      type: Number,
      default: 48,
    },
    mode: {
      type: String,
      default: "timeline",
    },
  },
  data() {
    return {
      transport: {},
      truckStyle: {},
      truckMoving: false,
    };
  },
  mounted() {
    this.setCompanyLeft();
    this.positionTruck();
  },
  beforeDestroy() {
    this.transport = {};
  },
  watch: {
    details(nv, ov) {
      if (nv) this.setCompanyLeft();
    },
    route: {
      deep: true,
      handler() {
        this.positionTruck();
      },
    },
    timelineWidth() {
      this.positionTruck();
    },
  },
  computed: {
    isTimeline() {
      return this.mode == "timeline";
    },
    isPending() {
      return this.mode == "pending";
    },
    timelineStart() {
      // deduct half width of spot svg
      return this.isTimeline
        ? `${this.route.operations[0].arrival * this.timelineWidth - 10}px`
        : "0px";
    },
    timelineRange() {
      return function (index) {
        if (index == 0) return 0;
        if (this.isTimeline) {
          let prev = this.route.operations[index - 1];
          let current = this.route.operations[index];
          let total = current.arrival - prev.arrival;
          return `${this._.round(total * this.timelineWidth - 20, 2)}px`;
        } else {
          return `${this.timelineWidth}px`;
        }
      };
    },
    durationRatio() {
      return function (index) {
        let prev = this.route.operations[index - 1];
        let current = this.route.operations[index];
        let total = current.arrival - prev.arrival;
        return this._.round((prev.duration / total) * 100.0, 2);
      };
    },
    hoursRatio() {
      return function (index) {
        return 100 - this.durationRatio(index);
      };
    },
    companyType() {
      return function (operation) {
        return operation?.company?.type?.toLowerCase();
      };
    },
    rawSvg() {
      return function (operation) {
        return this.$getRawSvg(this.companyType(operation) + "2");
      };
    },
    rawSvgColor() {
      return function (operation) {
        return operation.arrived ? this.rawSvg(operation).color : "gray";
      };
    },
  },
  methods: {
    positionTruck() {
      this.$nextTick(() => {
        const routeEl = this.$refs.route;
        const truckEl = this.$refs.truck;
        if (!routeEl || !truckEl) return;
        const truckImg = truckEl.querySelector("img");
        if (!truckImg) return;

        const routeRect = routeEl.getBoundingClientRect();
        const truckWidth = truckImg.getBoundingClientRect().width;
        const elems = routeEl.querySelectorAll(".ticked");
        const ticked = elems[elems.length - 1];
        if (!ticked) {
          this.truckMoving = false;
          this.truckStyle = {};
          return;
        }

        const rect = ticked.getBoundingClientRect();
        const left = rect.left - routeRect.left;
        const style = { left: `${left}px` };
        const moving = ticked.classList.contains("road");
        if (moving) {
          style.width = `${Math.max(
            truckWidth,
            ticked.clientWidth - truckWidth,
          )}px`;
        }
        this.truckStyle = style;
        this.truckMoving = moving;
      });
    },
    setCompanyLeft() {
      this.$nextTick(() => {
        document.querySelectorAll(".route .actions").forEach((action) => {
          let companies = action.querySelectorAll(".company");
          companies.forEach((company, index) => {
            if (index == 0) return;
            if (index == companies.length - 1) {
              company.style.marginLeft = `-${company.offsetWidth - 16}px`;
              return;
            }
            company.style.marginLeft = `-${company.offsetWidth / 2.0 - 8}px`;
          });
        });
      });
    },
    enterCompany(company) {
      if (company.id) {
        document.querySelectorAll(".action .stop svg").forEach((elem) => {
          elem.classList.remove("animate-bounce");
        });
        document
          .querySelectorAll(`.action .stop[data-id="${company.id}"] svg`)
          .forEach((elem) => {
            elem.classList.add("animate-bounce");
          });
      }
    },
    leaveCompany() {
      document.querySelectorAll(".action .stop svg").forEach((elem) => {
        elem.classList.remove("animate-bounce");
      });
    },
    openRoute() {
      this.$emit("openRoute", {
        id: this.route.id,
        container: this.route.container,
        mode: this.mode,
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.route {
  @apply absolute z-10;
  &.timeline {
    @apply hover:bg-sky-100 hover:rounded-md hover:z-50;
  }
  .truck-svg {
    @apply absolute -top-0.5 z-50;
    img {
      @apply h-4 w-7;
    }
  }
  .actions {
    @apply h-10 flex items-center flex-nowrap overflow-auto;
  }
  .action {
    @apply flex items-center;
    .progress {
      @apply flex items-center;
      .progress-bar {
        @apply border-t-2 border-gray-500 border-dashed;
        &.departed {
          @apply border-red-500 border-solid;
        }
        &.finished {
          @apply border-green-500 border-solid;
        }
      }
    }
    .stop {
      @apply h-5 relative;
      .icon {
        @apply w-5 h-5;
      }
      .company {
        @apply hidden;
      }
    }
  }
  &.details {
    .actions {
      @apply mt-0;
    }
    .stop {
      .company {
        @apply absolute block text-2xs h-3 leading-tight tracking-tighter text-nowrap max-w-24 truncate z-10;
        @apply left-0 -bottom-2;
        &:hover {
          @apply z-50 bg-white shadow font-bold px-0.5;
        }
      }
      &.even {
        .company {
          @apply -top-2.5;
        }
      }
    }
  }
  .abbrev-type {
    @apply absolute -bottom-0.5 left-1/2 -translate-x-1/2 text-2xs text-white font-bold bg-blue-500 px-0.5 border rounded shadow z-20;
  }
}
@keyframes truck-move {
  0% {
    left: 0;
  }
  100% {
    left: 100%;
  }
}
.truck-move {
  @apply absolute;
  animation: truck-move 7.5s linear infinite;
}
</style>
